import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import { Pagination } from '../../../shared/components/Pagination';
import TableSkeleton, { ITableColumn } from '../../../shared/components/TableSkeleton';
import { ErrorToast } from '../../../utils/toasters';
import { DiscoveryPresetService } from '../DiscoveryPresetService';
import { IDiscoveryPreset } from '../../../model/discovery/DiscoveryPresets';
import SingleSelectDropdown, { toSelectValue } from '../../../shared/components/SingleSelectDropdown';
import { Platform } from '../../../model/platform/Platform';
import { PlatformService } from '../../platform/PlatformService';
import { IPlatformFilteringParams } from '../../platform/dto/IPlatformFilteringParams';
import { getGrowthIntervalLabel } from '../dto/follower-interval.dto';

const PRESET_COLUMNS: ITableColumn[] = [
    { label: 'Title' },
    { label: 'Platform' },
    { label: 'Influencer filter' },
    { label: 'Audience filter' },
    { label: '' },
];

export default function DiscoveryFilterPresets() {
    const [state, setState] = useState({
        page: 1,
        perPage: 15,
        title: '',
        platformId: undefined,
    });

    const [discoveryPresets, setDiscoveryPresets] = useState<IDiscoveryPreset[]>([]);
    const [change, setChange] = useState(false);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [dataLoading, setDataLoading] = useState(true);
    const [platformDropdown, setPlatformDropdown] = useState<Platform[]>([]);

    useEffect(() => {
        setDataLoading(true);
        DiscoveryPresetService.getDiscoveryPresets(state)
            .then(data => {
                setDiscoveryPresets(data.data);
                setTotalPages(Math.ceil(data.count / state.perPage));
            })
            .catch(err => ErrorToast(err))
            .finally(() => setDataLoading(false));
    }, [change, page]);

    useEffect(() => {
        PlatformService.getPlatforms({ hasApi: true } as IPlatformFilteringParams).then(data => {
            setPlatformDropdown(data.data);
        });
    }, []);

    function handleClick(event: any) {
        event.preventDefault();
        setState(prev => ({ ...prev, page: 1 }));
        setPage(1);
        setChange(!change);
    }

    function handleChange(event: any) {
        const value = event.target.value.trim();
        setState({
            ...state,
            [event.target.name]: value,
        });
    }

    const handlePages = (updatePage: number) => {
        setState({ ...state, page: updatePage });
        setPage(updatePage);
    };

    return (
        <>
            <Card
                className="mb-5 p-4 mt-3"
                onKeyDown={e => {
                    if (e.key === 'Enter') handleClick(e);
                }}
            >
                <Row>
                    <Form.Group as={Col} className="col-4 mb-0">
                        <Form.Control
                            placeholder="Title"
                            className="autocomplete-input"
                            name="title"
                            onChange={handleChange}
                        />
                    </Form.Group>
                    <div className="col-md-2 col-6">
                        <SingleSelectDropdown
                            placeholder="All platforms"
                            options={platformDropdown.map(item => ({
                                value: String(item.id),
                                label: item.name,
                                image: item.path,
                            }))}
                            withImage
                            selected={toSelectValue(state.platformId)}
                            onChange={value =>
                                setState((prev: any) => ({ ...prev, platformId: value ? +value : undefined }))
                            }
                        />
                    </div>
                    <Form.Group as={Col} className="mb-0">
                        <Button type="submit" onClick={handleClick}>
                            Search
                        </Button>
                    </Form.Group>
                </Row>
            </Card>
            <Card className="mb-5 p-4">
                {dataLoading ? (
                    <TableSkeleton columns={PRESET_COLUMNS} rows={state.perPage} />
                ) : (
                    <Table hover responsive>
                        <thead>
                            <tr>
                                {PRESET_COLUMNS.map(column => (
                                    <th key={column.label} className={column.className}>
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {discoveryPresets.map(preset => {
                                const ui = preset.uiDefaults || {};
                                const influencer = ui.influencer || {};
                                const audience = ui.audience || {};
                                const filterData = preset?.filterData;
                                const influencerFilter = filterData?.influencer || {};

                                return (
                                    <tr key={preset.id}>
                                        <td>{preset.title}</td>
                                        <td>
                                            {preset.platform?.path ? (
                                                <img
                                                    alt=""
                                                    src={preset.platform.path}
                                                    style={{ width: 35, height: 35 }}
                                                />
                                            ) : (
                                                <img
                                                    alt=""
                                                    src="/images/placeholder.png"
                                                    style={{ width: 35, height: 35 }}
                                                />
                                            )}{' '}
                                            {preset.platform?.name}
                                        </td>

                                        <td style={{ fontSize: '0.9rem' }}>
                                            {influencer.location && influencer.location.length > 0 && (
                                                <div>
                                                    <strong>Location:</strong>{' '}
                                                    {influencer.location.map(loc => loc.title).join(', ')}
                                                </div>
                                            )}
                                            {influencerFilter?.followers && (
                                                <div>
                                                    <strong>Follower range:</strong> {influencerFilter?.followers?.min}{' '}
                                                    - {influencerFilter?.followers?.max}
                                                </div>
                                            )}
                                            {influencerFilter?.reelsPlays && (
                                                <div>
                                                    <strong>Average views range:</strong>{' '}
                                                    {influencerFilter?.reelsPlays?.min} -{' '}
                                                    {influencerFilter?.reelsPlays?.max}
                                                </div>
                                            )}
                                            {influencerFilter.age && (
                                                <div>
                                                    <strong>Age range:</strong> {influencerFilter?.age?.min} -{' '}
                                                    {influencerFilter?.age?.max}
                                                </div>
                                            )}
                                            {influencerFilter.followersGrowthRate && (
                                                <div>
                                                    <strong>Followers growth rate:</strong>{' '}
                                                    {influencerFilter?.followersGrowthRate?.value * 100}% -{' '}
                                                    {getGrowthIntervalLabel(
                                                        influencerFilter.followersGrowthRate.interval
                                                    )}
                                                </div>
                                            )}
                                            {influencer.language && (
                                                <div>
                                                    <strong>Language:</strong> {influencer.language.name}
                                                </div>
                                            )}
                                            {influencer.engagementRate !== undefined && (
                                                <div>
                                                    <strong>Engagement rate:</strong>≥{' '}
                                                    {(influencer.engagementRate * 100).toFixed(2)}%
                                                </div>
                                            )}
                                            {influencer.gender && (
                                                <div>
                                                    <strong>Gender:</strong> {influencer.gender}
                                                </div>
                                            )}
                                            {influencer?.lastposted && (
                                                <div>
                                                    <strong>Last posted:</strong> {influencer?.lastposted} days
                                                </div>
                                            )}
                                            {influencer?.interests && influencer?.interests?.length > 0 && (
                                                <div>
                                                    <strong>Categories:</strong>{' '}
                                                    {influencer?.interests.map(i => i.name).join(', ')}
                                                </div>
                                            )}
                                            {influencer?.hashtags && influencer?.hashtags?.length > 0 && (
                                                <div>
                                                    <strong>Hashtags:</strong> {influencer?.hashtags.join(', ')}
                                                </div>
                                            )}
                                            {influencer?.mentions && influencer?.mentions?.length > 0 && (
                                                <div>
                                                    <strong>Mentions:</strong>{' '}
                                                    {influencer?.mentions.map(i => i.username).join(', ')}
                                                </div>
                                            )}
                                            {influencer?.topics && influencer?.topics?.length > 0 && (
                                                <div>
                                                    <strong>Topics:</strong> {influencer?.topics.join(', ')}
                                                </div>
                                            )}
                                            {influencerFilter.bio && (
                                                <div>
                                                    <strong>Bio: </strong> {influencerFilter?.bio}
                                                </div>
                                            )}
                                            {audience?.credibility && (
                                                <div>
                                                    <strong>Fake follower rate: </strong>≤{' '}
                                                    {(100 - audience?.credibility * 100).toFixed(0)}%
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ fontSize: '0.9rem' }}>
                                            {audience.location && audience.location.length > 0 && (
                                                <div>
                                                    <strong>Location:</strong>{' '}
                                                    {audience.location.map(loc => loc.title).join(', ')}
                                                </div>
                                            )}
                                            {audience.gender && (
                                                <div>
                                                    <strong>Gender:</strong> {audience.gender.id} (≥{' '}
                                                    {(audience.gender.weight * 100).toFixed(0)}%)
                                                </div>
                                            )}
                                            {audience?.age && audience?.age?.length > 0 && (
                                                <div>
                                                    <strong>Age:</strong> {audience?.age.join(', ')}
                                                </div>
                                            )}
                                            {audience?.language?.id && (
                                                <div>
                                                    <strong>Language:</strong> {audience?.language?.id?.name} (≥{' '}
                                                    {(audience?.language.weight * 100).toFixed(0)}%)
                                                </div>
                                            )}
                                        </td>
                                        <td></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                )}

                <div className="w-100">
                    {totalPages <= 1 ? (
                        ''
                    ) : (
                        <Pagination
                            page={page}
                            totalPages={totalPages}
                            handlePagination={handlePages}
                            disabled={dataLoading}
                        />
                    )}
                </div>
            </Card>
        </>
    );
}
